// src/components/settings/follow-up-boss-tab.jsx
//
// Owner-level "Connect Follow Up Boss" screen. The team runs on ONE Follow Up
// Boss account (owner + team members as users under it), so the owner connects
// ONCE here with their owner API key — that single connection covers the whole
// team. Stored in ONE team-wide doc (integrations/followUpBoss) so the backend
// helper (functions-fub) reads it without scanning users.
//
// Admin-only (see main-settings tab role). The owner key sees every person in
// the account, so the helper routes each lead to the right agent by FUB's
// "assigned user". Keep Firestore rules on integrations/** restricted to admins.
// Never log the raw key.
import React, { useEffect, useState } from "react";
import { formatDate } from "../../utils/helper";
import { toast } from "react-toastify";
import { Loader2, CheckCircle2, KeyRound } from "lucide-react";
import { useStateValue } from "../../context/StateProvider";
import {
  getFubIntegration,
  saveFubIntegration,
  registerFubWebhooks,
} from "../../utils/firebasefunctions";

export default function FollowUpBossTab() {
  const [{ user }] = useStateValue();
  const [integ, setInteg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [activating, setActivating] = useState(false);
  const [systemName, setSystemName] = useState("TeamEdelstein");
  const [systemKey, setSystemKey] = useState("");

  const load = async () => {
    const data = await getFubIntegration();
    setInteg(data || {});
    setTagInput(data?.syncTag || "TeamEdelstein");
    setSystemName(data?.systemName || "TeamEdelstein");
    setSystemKey("");
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const connected = !!integ?.apiKey;
  const syncOn = integ?.syncEnabled !== false;

  const connect = async () => {
    const key = apiKey.trim();
    if (!key) {
      toast.error("Paste your Follow Up Boss API key first.");
      return;
    }
    setBusy(true);
    const ok = await saveFubIntegration({
      apiKey: key,
      syncEnabled: true,
      syncTag: tagInput.trim() || "TeamEdelstein",
      connectedBy: user?.id || "",
      connectedAt: Date.now(),
    });
    if (ok) {
      await load();
      setApiKey("");
      toast.success("Follow Up Boss connected.");
    } else {
      toast.error("Could not save — please try again.");
    }
    setBusy(false);
  };

  const disconnect = async () => {
    setBusy(true);
    const ok = await saveFubIntegration({
      apiKey: "",
      syncEnabled: false,
      connectedBy: "",
      connectedAt: 0,
    });
    if (ok) {
      await load();
      toast.success("Follow Up Boss disconnected.");
    } else {
      toast.error("Could not disconnect — please try again.");
    }
    setBusy(false);
  };

  const toggleSync = async () => {
    const next = !syncOn;
    setBusy(true);
    const ok = await saveFubIntegration({ syncEnabled: next });
    if (ok) await load();
    else toast.error("Could not update.");
    setBusy(false);
  };

  const saveTag = async () => {
    setBusy(true);
    const ok = await saveFubIntegration({ syncTag: tagInput.trim() });
    if (ok) {
      await load();
      toast.success("Sync tag updated.");
    } else {
      toast.error("Could not update.");
    }
    setBusy(false);
  };

  const activate = async () => {
    setActivating(true);
    const r = await registerFubWebhooks();
    if (r?.ok) {
      const n = r.created?.length || 0;
      toast.success(
        n
          ? `Live sync activated (${n} event${n === 1 ? "" : "s"}).`
          : "Live sync is already active.",
      );
    } else {
      toast.error(
        "Could not activate — make sure your System Key is saved (Follow Up Boss requires a registered system).",
      );
    }
    setActivating(false);
  };

  const saveSystem = async () => {
    setBusy(true);
    const patch = { systemName: systemName.trim() || "TeamEdelstein" };
    if (systemKey.trim()) patch.systemKey = systemKey.trim();
    const ok = await saveFubIntegration(patch);
    if (ok) {
      await load();
      setSystemKey("");
      toast.success("Integration key saved.");
    } else {
      toast.error("Could not save — please try again.");
    }
    setBusy(false);
  };

  const maskedKey = integ?.apiKey
    ? `••••••••${String(integ.apiKey).slice(-4)}`
    : "";
  const connectedDate = integ?.connectedAt
    ? formatDate(integ.connectedAt)
    : "";

  return (
    <div className="rounded-xl border border-gray-100 bg-white shadow-sm p-6 max-w-2xl">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-1">
        Follow Up Boss
      </h2>
      <p className="text-sm text-gray-500 mb-4">
        Connect your team's Follow Up Boss once. As the account owner, this single
        connection syncs every agent's leads both ways — your team members don't
        need to set up anything, and each lead routes to the right agent
        automatically.
      </p>

      {/* Honest status: connecting here readies the account; live sync needs the
          backend helper, which isn't deployed yet. */}
      <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 mb-4">
        To go live: connect your API key, add your Follow Up Boss System Key
        (register once — see below), set your Sync Tag, then click Activate Live
        Sync.
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : connected ? (
        <>
        <div className="rounded-xl border border-green-500 bg-green-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-green-900 text-xs font-semibold">✓ CONNECTED</p>
              <p className="text-black font-semibold text-sm">
                API Key {maskedKey}
              </p>
              <p className="text-gray-500 text-xs">
                {connectedDate ? `Connected ${connectedDate}` : "Connected"} ·{" "}
                {syncOn ? "Sync enabled" : "Sync paused"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={toggleSync}
              disabled={busy}
              className="rounded-lg border border-gray-200 bg-white shadow-sm px-3 py-2 text-sm text-gray-600 hover:border-gray-300 disabled:opacity-50"
            >
              {syncOn ? "Pause Sync" : "Enable Sync"}
            </button>
            <button
              onClick={disconnect}
              disabled={busy}
              className="rounded-lg border border-gray-200 bg-white shadow-sm px-3 py-2 text-sm text-gray-600 hover:border-red-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-50"
            >
              {busy ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Working…
                </span>
              ) : (
                "Disconnect"
              )}
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Which Leads Sync
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Only Follow Up Boss leads with this tag sync into your pipeline — so
            you're never flooded with every lead. Add this tag in Follow Up Boss
            to the leads you want; leave the rest out. (Add a "Seller" tag too to
            send one to your Listing Pipeline.)
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Outbound is opt-in too: a lead only pushes <b>to</b> Follow Up Boss
            when its "Sync to Follow Up Boss" switch is on (on the lead's card,
            or set when creating it). Everything else stays out of FUB.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              placeholder="e.g., TeamEdelstein"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={saveTag}
              disabled={busy}
              className="rounded-lg border border-gray-200 bg-white shadow-sm px-4 py-2 text-sm text-gray-600 hover:border-gray-300 disabled:opacity-50"
            >
              Save Tag
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Integration Key (Required for Live Sync)
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Follow Up Boss requires a registered "system" before it allows live
            sync. Register once at{" "}
            <b>apps.followupboss.com/system-registration</b> (name it
            "TeamEdelstein"), then paste the System Key it gives you here.
            {integ?.systemKey ? " ✓ A key is saved." : ""}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={systemName}
              onChange={(e) => setSystemName(e.target.value)}
              placeholder="System Name"
              className="sm:w-44 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <input
              type="password"
              value={systemKey}
              onChange={(e) => setSystemKey(e.target.value)}
              placeholder={
                integ?.systemKey
                  ? "•••••• (saved — paste to replace)"
                  : "Paste System Key"
              }
              autoComplete="off"
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
            <button
              onClick={saveSystem}
              disabled={busy}
              className="rounded-lg border border-gray-200 bg-white shadow-sm px-4 py-2 text-sm text-gray-600 hover:border-gray-300 disabled:opacity-50"
            >
              Save Key
            </button>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-gray-200 p-4">
          <p className="text-sm font-semibold text-gray-700 mb-1">
            Activate Live Sync
          </p>
          <p className="text-xs text-gray-500 mb-2">
            Turns on the live connection so tagged leads sync the moment they
            change in Follow Up Boss. Safe to click again anytime.
          </p>
          <button
            onClick={activate}
            disabled={activating}
            className="inline-flex items-center gap-2 bg-[#1E293B] hover:opacity-90 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50"
          >
            {activating ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Activate Live Sync
          </button>
        </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-300 p-4 space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Follow Up Boss API Key
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <KeyRound className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste your API key"
                autoComplete="off"
                className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
              />
            </div>
            <button
              onClick={connect}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 bg-[#1E293B] hover:opacity-90 text-white rounded-lg px-5 py-2 text-sm font-semibold disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Connect
            </button>
          </div>
          <p className="text-xs text-gray-500">
            In Follow Up Boss, open <b>Admin → API</b> (top-right menu), create a
            new API key, and paste it here. Use <i>your owner</i> key — it covers
            your whole team in one connection.
          </p>
        </div>
      )}
    </div>
  );
}
